// CRO-38 — promo code entry in the mini-bag.
// /cart/update.js returns 200 for unusable codes — only discount_codes[].applicable
// says otherwise — and each call replaces the whole code list instead of adding to it.

(function () {
  'use strict';

  // Mirrors CartDrawerItems.getSectionsToRender() in cart.js.
  var SECTIONS = [
    { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
  ];

  function t(key, code) {
    var value = (window.cartStrings && window.cartStrings[key]) || '';
    return code ? value.replace('[code]', code) : value;
  }

  function sameCode(a, b) {
    return String(a).toLowerCase() === String(b).toLowerCase();
  }

  class MiniBagDiscount extends HTMLElement {
    connectedCallback() {
      this.pending = false;
      this.input = this.querySelector('.mini-bag-discount__input');
      this.messageEl = this.querySelector('[data-discount-message]');
      this.submitButton = this.querySelector('[data-discount-submit]');

      if (this.submitButton) {
        this.submitButton.addEventListener('click', () => this.onSubmit());
      }

      if (this.input) {
        this.input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.onSubmit();
        });
      }

      this.addEventListener('click', (event) => {
        var remove = event.target.closest('[data-discount-remove]');
        if (!remove) return;
        event.preventDefault();
        this.onRemove(remove.getAttribute('data-discount-remove'));
      });
    }

    appliedCodes() {
      var raw = this.getAttribute('data-applied') || '';
      return raw.split(',').filter(Boolean);
    }

    onSubmit() {
      // Enter and the chip remove buttons are not covered by the disabled Apply
      // button, and both actions read the same data-applied list.
      if (this.pending) return;

      // A comma would be read as a code separator by /cart/update.js.
      var code = (this.input.value || '').trim().replace(/,/g, '');
      if (!code) {
        this.setMessage(t('discountEmpty'), 'error');
        this.input.focus();
        return;
      }

      var applied = this.appliedCodes();
      if (applied.some((existing) => sameCode(existing, code))) {
        this.setMessage(t('discountApplied', code), 'success');
        this.input.value = '';
        return;
      }

      this.pending = true;
      this.post(applied.concat(code).join(','))
        .then((state) => {
          var returned = state.discount_codes || [];
          var accepted = returned.some((d) => sameCode(d.code, code) && d.applicable);

          if (!accepted) {
            this.input.select();
            // The rejected code replaced the existing ones, so put them back; with
            // none this posts '', clearing it off the cart. post() wipes the message.
            return this.post(applied.join(',')).then(() =>
              this.setMessage(t('discountInvalid'), 'error')
            );
          }

          return this.renderSections(state, t('discountApplied', code));
        })
        .catch(() => this.setMessage(t('discountError'), 'error'))
        .finally(() => {
          this.pending = false;
          this.setLoading(false);
        });
    }

    onRemove(code) {
      if (this.pending) return;
      var remaining = this.appliedCodes().filter((existing) => !sameCode(existing, code));

      this.pending = true;
      this.post(remaining.join(','))
        .then((state) => {
          return this.renderSections(state, t('discountRemoved', code));
        })
        .catch(() => this.setMessage(t('discountError'), 'error'))
        .finally(() => {
          this.pending = false;
          this.setLoading(false);
        });
    }

    post(discount) {
      this.setLoading(true);
      this.setMessage('');

      var body = JSON.stringify({
        discount: discount,
        sections: SECTIONS.map((section) => section.section),
        sections_url: window.location.pathname,
      });

      return fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body,
      }).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      });
    }

    // Destroys this element and the live region with it, hence announcing after.
    renderSections(state, message) {
      if (!state.sections) {
        this.setMessage(message, 'success');
        return;
      }

      var drawerRendered = false;
      SECTIONS.forEach((section) => {
        var container = document.getElementById(section.id);
        if (!container) return;
        var target = container.querySelector(section.selector) || container;
        var html = new DOMParser()
          .parseFromString(state.sections[section.section], 'text/html')
          .querySelector(section.selector);
        if (!html) return;
        target.innerHTML = html.innerHTML;
        if (section.id === 'CartDrawer') drawerRendered = true;
      });

      // 'cart-items' so CartDrawerItems.onCartUpdate skips refetching what is already current.
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      }

      // Announcing success off a drawer that did not take the markup would be a lie.
      if (drawerRendered) {
        this.announce(message);
      } else {
        this.setMessage(t('discountError'), 'error');
      }
    }

    setLoading(isLoading) {
      if (!this.isConnected || !this.submitButton) return;
      this.classList.toggle('mini-bag-discount--loading', isLoading);
      this.submitButton.disabled = isLoading;
    }

    setMessage(message, state) {
      if (!this.isConnected || !this.messageEl) return;
      this.messageEl.textContent = message;
      this.messageEl.classList.toggle('mini-bag-discount__message--error', state === 'error');
      this.messageEl.classList.toggle('mini-bag-discount__message--success', state === 'success');
    }

    announce(message) {
      if (!message) return;
      var region = document.getElementById('CartDrawer-LiveRegionText');
      if (!region) return;
      region.textContent = message;
      region.setAttribute('aria-hidden', false);
      setTimeout(() => region.setAttribute('aria-hidden', true), 1000);
    }
  }

  if (!customElements.get('mini-bag-discount')) {
    customElements.define('mini-bag-discount', MiniBagDiscount);
  }
})();
